// src/composables/useListFilters.js

import { ref, watch, computed } from '@vue/composition-api'
import router from '@/router'

/**
 * Reactive URL-syncing filters hook
 * @param {Array} defs Array of { param, default, parse?, transform? }
 * @param {Function} reloadFn Called whenever filters change
 * @returns {{ filterRefs: Object, filterParams: ComputedRef, initFromRoute: Function }}
 */
export function useListFilters(defs, reloadFn) {
	// 1) Create a ref for each filter
	const filterRefs = {}
	defs.forEach(({ param, default: defVal }) => {
		filterRefs[param] = ref(defVal)
	})

	// 2) Initialize from current URL query
	function initFromRoute() {
		const query = router.currentRoute.query
		defs.forEach(({ param, parse }) => {
			if (query[param] !== undefined) {
				filterRefs[param].value = parse
					? parse(query[param])
					: (isNaN(query[param]) ? query[param] : parseInt(query[param], 10))
			}
		})
	}
	initFromRoute()

	// 3) Compute outgoing query params, applying transforms and omitting defaults
	const filterParams = computed(() => {
		const q = {}
		defs.forEach(({ param, default: defVal, transform }) => {
			const raw = filterRefs[param].value
			const out = transform ? transform(raw) : raw
			if (out !== defVal && out !== '' && out != null) {
				q[param] = out
			}
		})
		return q
	})

	// 4) Watch and sync
	watch(
		filterParams,
		q => {
			// use history.pushState via router helper for no full reload
			router.addParamsNoRefresh(q)
			if (reloadFn) reloadFn()
		},
		{ immediate: true }
	)

	return { filterRefs, filterParams, initFromRoute }
}
